import { useEffect, useRef, useState } from "react";
import type { Browser } from "igv";
import { Loader2 } from "lucide-react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { cn } from "~/lib/utils";

const GENOMICS_EXTENSIONS = new Set(["bam", "cram"]);

const MAX_GENOMICS_FILE_BYTES = 500 * 1024 * 1024; // 500 MB

export function isGenomicsAlignmentFile(fileName: string): boolean {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  return GENOMICS_EXTENSIONS.has(ext);
}

interface IgvViewerProps {
  artifactId: string;
  fileName: string;
}

export function IgvViewer({ artifactId, fileName }: IgvViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const browserRef = useRef<Browser | null>(null);
  const igvRef = useRef<{ removeBrowser: (b: Browser) => void } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { api, conversationId } = useAssistantContext();

  useEffect(() => {
    if (!containerRef.current || !conversationId || !artifactId) return;

    let cancelled = false;

    const loadViewer = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const artifact = await api.getArtifactMetadata(conversationId, artifactId);
        const downloadUrl = artifact.download_url;

        const indexArtifactId = artifact.custom_metadata?.index_artifact_id as string | undefined;
        const referenceGenome =
          (artifact.custom_metadata?.reference_genome as string | undefined) ?? "hg38";
        if (!indexArtifactId) {
          throw new Error(
            "No companion index file found. BAM/CRAM files require a .bai/.crai index in the same location."
          );
        }

        const indexUrl = await api.getArtifactDownloadUrl(conversationId, indexArtifactId);

        const fileSizeBytes = artifact.custom_metadata?.file_size_bytes as number | undefined;
        if (fileSizeBytes && fileSizeBytes > MAX_GENOMICS_FILE_BYTES) {
          throw new Error("File is too large to render inline. Please download it instead.");
        }

        if (cancelled) return;

        const { default: igv } = await import("igv");
        igvRef.current = igv;

        if (cancelled || !containerRef.current) return;

        if (browserRef.current) {
          igv.removeBrowser(browserRef.current);
          browserRef.current = null;
        }

        const browser = await igv.createBrowser(containerRef.current, {
          genome: referenceGenome,
          tracks: [
            {
              type: "alignment",
              format: fileName.toLowerCase().endsWith(".cram") ? "cram" : "bam",
              url: downloadUrl,
              indexURL: indexUrl,
              name: fileName,
            },
          ],
        });

        if (cancelled) {
          igv.removeBrowser(browser);
          return;
        }

        browserRef.current = browser;
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load IGV viewer:", err);
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadViewer();

    return () => {
      cancelled = true;
      if (browserRef.current && igvRef.current) {
        igvRef.current.removeBrowser(browserRef.current);
        browserRef.current = null;
      }
    };
  }, [api, conversationId, artifactId, fileName]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-500">
        {error}
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-[500px]">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="text-center">
            <Loader2 className="size-8 text-gray-400 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading alignment viewer...</p>
          </div>
        </div>
      )}
      <div ref={containerRef} className={cn("w-full rounded-md", isLoading && "invisible")} />
    </div>
  );
}
